import { SignInRequired } from "../../auth/signIn";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Calendar, Card, Col, Empty, List, Radio, Row, Select, Typography, theme } from "antd";
import type { CalendarProps } from "antd";
import { EmptyState, ErrorState, LoadingState, PageHeader, StatTile } from "@dowty/ui";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { useAuth } from "../../context/AuthContext";
import { usePending } from "@dowty/offline";
import { outbox } from "../../offline";
import { DrinkActivitySummary } from "../../services/drinkService";
import { fetchDrinkActivities } from "../../services/drinks/DrinkService";
import { toAbvPercent } from "../../services/drinks/utils/numbers";
import { CardHeading, ListItemMeta } from "../ListItemMeta";
import { getLocalDayKey, groupActivitiesByDay } from "./drinkHistory/dayBucketing";
import { useTranslation } from "react-i18next";
import "./AnalyticsPages.css";
import { useAppFeedback } from "../useAppFeedback";

const { Text } = Typography;

const DrinkHistory: React.FC = () => {
    const { message } = useAppFeedback();
    const { isAuthenticated, user } = useAuth();
    const { token } = theme.useToken();
    const { t, i18n } = useTranslation();

    const [loading, setLoading] = useState<boolean>(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [activities, setActivities] = useState<DrinkActivitySummary[]>([]);
    // Drinks still in the offline outbox are listed as pending, with their expected values, until the server list contains them.
    const pendingAndListed = usePending(activities, outbox, {
        toOptimistic: ({ drink, amount, tsInsert, tsStart, eventId, user: owner }): DrinkActivitySummary => ({
            pendingUserId: owner.userid,
            amountMl: amount,
            drinkId: drink.drinkid,
            drinkName: drink.drinkname,
            promille: drink.promille,
            timestamp: new Date(tsInsert),
            eventId: eventId ?? null,
            startedAt: tsStart === undefined ? null : new Date(tsStart),
            durationMinutes: null,
        }),
        matches: (item, { payload }) =>
            item.drinkId === payload.drink.drinkid &&
            item.amountMl === payload.amount &&
            Math.floor((item.timestamp?.getTime() ?? 0) / 1000) === Math.floor(payload.tsInsert / 1000),
    });
    const listed = pendingAndListed.filter(item => !item.pending || item.pendingUserId === user?.userId);
    const [selectedDay, setSelectedDay] = useState<Dayjs>(() => dayjs().startOf("day"));

    const loadActivities = useCallback(async () => {
        if (!user) {
            return;
        }
        setLoading(true);
        setLoadError(null);
        try {
            const response = await fetchDrinkActivities(user);
            setActivities(response);
        } catch {
            const details = t("drinkHistory.messages.loadActivitiesError");
            setLoadError(details);
            message.error(details);
        } finally {
            setLoading(false);
        }
    }, [user, t, message]);

    useEffect(() => {
        if (isAuthenticated && user) {
            void loadActivities();
        } else {
            setActivities([]);
        }
    }, [isAuthenticated, user, loadActivities]);

    useEffect(() => outbox.subscribe((event) => event.type === "confirmed" && void loadActivities()), [loadActivities]);

    // Local-day buckets keyed the same way the calendar cells and selected-day
    // lookup key themselves, so a drink logged at 23:30 stays on the viewer's
    // local calendar day rather than shifting under UTC bucketing.
    const dayMap = groupActivitiesByDay(listed);

    const selectedDayKey = useMemo(() => getLocalDayKey(selectedDay.toDate()), [selectedDay]);
    const selectedDaySummary = dayMap.get(selectedDayKey);
    const selectedDayEntries = selectedDaySummary?.entries ?? [];

    // antd doesn't export CellRenderInfo from its package root, so the parameter type is derived
    // from CalendarProps itself rather than re-declared by hand. That keeps this honestly typed
    // (no `as any` at the call site) and means a future antd change to the signature surfaces
    // here as a compile error instead of being silently swallowed by a cast.
    const cellRender = useCallback<NonNullable<CalendarProps<Dayjs>["cellRender"]>>(
        (current, info) => {
            if (info.type !== "date") {
                return info.originNode;
            }
            const dayKey = getLocalDayKey(current.toDate());
            const summary = dayMap.get(dayKey);
            if (!summary) {
                return null;
            }
            return (
                <div style={{ textAlign: "center" }}>
                    <Badge
                        count={summary.entries.length}
                        style={{ backgroundColor: token.colorPrimary, color: token.colorTextLightSolid }}
                        title={t("drinkHistory.calendar.badgeTooltip", {
                            count: summary.entries.length,
                            amount: summary.totalMl,
                        })}
                    />
                </div>
            );
        },
        [dayMap, token.colorPrimary, token.colorTextLightSolid, t]
    );
    const calendarHeaderRender: NonNullable<CalendarProps<Dayjs>["headerRender"]> = ({ value, type, onChange, onTypeChange }) => (
        <div className="history-calendar-header">
            <Button className="history-calendar-nav" aria-label={t("common.previousPage")} onClick={() => onChange(value.add(-1, type))}>‹</Button>
            <label className="ui-sr-only" htmlFor="drink-history-year">{t("drinkHistory.calendar.yearLabel")}</label>
            <Select
                    id="drink-history-year"
                    value={value.year()}
                    options={Array.from({ length: 20 }, (_, index) => value.year() - 10 + index).map((year) => ({ value: year, label: year }))}
                    onChange={(year) => onChange(value.year(year))}
                />
            {type === "month" && <>
                <label className="ui-sr-only" htmlFor="drink-history-month">{t("drinkHistory.calendar.monthLabel")}</label>
                <Select
                    id="drink-history-month"
                    value={value.month()}
                    options={Array.from({ length: 12 }, (_, month) => ({
                        value: month,
                        label: new Intl.DateTimeFormat(i18n.language, { month: "short" }).format(value.month(month).toDate()),
                    }))}
                    onChange={(month) => onChange(value.month(month))}
                />
            </>}
            <Button className="history-calendar-nav" aria-label={t("common.nextPage")} onClick={() => onChange(value.add(1, type))}>›</Button>
            <Radio.Group value={type} onChange={(event) => onTypeChange(event.target.value)}>
                <Radio.Button value="month">{t("drinkHistory.calendar.monthMode")}</Radio.Button>
                <Radio.Button value="year">{t("drinkHistory.calendar.yearMode")}</Radio.Button>
            </Radio.Group>
        </div>
    );

    if (!isAuthenticated || !user) {
        return <SignInRequired title={t("drinkHistory.authRequired.title")} description={t("drinkHistory.authRequired.description")} />;
    }

    return (
        <div className="analytics-page drink-history-page">
            <PageHeader title={t("drinkHistory.page.title")} subtitle={t("drinkHistory.page.subtitle")} />

            {!loading && !loadError && listed.length > 0 && <Row gutter={[12, 12]} className="history-summary">
                <Col xs={8}><StatTile label={t("drinkHistory.summary.days")} value={dayMap.size} /></Col>
                <Col xs={8}><StatTile label={t("drinkHistory.summary.drinks")} value={listed.length} /></Col>
                <Col xs={8}><StatTile label={t("drinkHistory.summary.volume")} value={listed.reduce((total, item) => total + item.amountMl, 0)} unit="ml" /></Col>
            </Row>}

            {loadError && <ErrorState message={loadError} retry={() => void loadActivities()} retryLabel={t("drinkHistory.actions.retry")} />}
            {loading && <LoadingState variant="list" label={t("drinkHistory.loading")} />}
            {!loading && !loadError && listed.length === 0 && (
                <EmptyState title={t("drinkHistory.empty.title")} description={t("drinkHistory.empty.description")} />
            )}
            {!loading && !loadError && listed.length > 0 && (
                <Row gutter={[16, 16]} className="history-content">
                    <Col xs={24} lg={16}>
                        <Card className="history-calendar-card">
                            <section className="history-calendar-scroll" tabIndex={0} aria-label={t("drinkHistory.calendar.scrollRegion")}>
                                <Calendar
                                    value={selectedDay}
                                    cellRender={cellRender}
                                    headerRender={calendarHeaderRender}
                                    onSelect={(value) => setSelectedDay(value.startOf("day"))}
                                />
                            </section>
                        </Card>
                    </Col>
                    <Col xs={24} lg={8}>
                        <Card className="history-day-card"
                            title={(
                                <CardHeading>
                                    {t("drinkHistory.dayPanel.titleWithDate", {
                                        date: selectedDay.toDate().toLocaleDateString(i18n.language, { dateStyle: "medium" }),
                                    })}
                                </CardHeading>
                            )}
                        >
                            {selectedDayEntries.length === 0 ? (
                                <Empty
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    description={t("drinkHistory.dayPanel.emptyDay", {
                                        date: selectedDay.toDate().toLocaleDateString(i18n.language, { dateStyle: "medium" }),
                                    })}
                                />
                            ) : (
                                <>
                                    <List
                                        className="history-entry-list"
                                        dataSource={selectedDayEntries}
                                        rowKey={(item) =>
                                            item.activityId
                                                ? String(item.activityId)
                                                : `${item.drinkId ?? "unknown"}-${item.timestamp?.getTime() ?? 0}`
                                        }
                                        renderItem={(item) => (
                                            <List.Item>
                                                <ListItemMeta
                                                    title={t("drinkHistory.dayPanel.entryTitle", {
                                                        drink: item.drinkName,
                                                        amount: item.amountMl,
                                                    })}
                                                >
                                                    {item.timestamp ? dayjs(item.timestamp).format("HH:mm") : ""}
                                                    {item.promille
                                                        ? ` · ${toAbvPercent(item.promille).toFixed(1)}% ABV`
                                                        : ""}
                                                    {item.pending && " · " + t(`drinkHistory.dayPanel.${item.pending}`)}
                                                </ListItemMeta>
                                            </List.Item>
                                        )}
                                    />
                                    <Text type="secondary">
                                        {t("drinkHistory.dayPanel.totalLabel", {
                                            amount: selectedDaySummary?.totalMl ?? 0,
                                        })}
                                    </Text>
                                </>
                            )}
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default DrinkHistory;
